import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  Tabs,
  Tab,
  Box,
  Chip,
  LinearProgress,
  TextField,
  Paper,
  Button,
  Divider
} from '@mui/material';
import { Star, CalendarToday } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { getFileInfo } from '../utils/fileTypeDetector';
import '../styles/ContractDetails.css';

const ClientTodos = () => {
  const { contractId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('All');
  
  // Use -30 to +30 days range as default
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const thirtyDaysFromNow = new Date();
  thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
  
  const [startDate, setStartDate] = useState(thirtyDaysAgo);
  const [endDate, setEndDate] = useState(thirtyDaysFromNow);

  // Status options for filtering
  const statusOptions = ['All', 'Working', 'Waiting on Client', 'Delivered'];

  // Helper function to format dates without date-fns
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Helper function to format date for display
  const formatDateForDisplay = (date) => {
    return date.toLocaleDateString('en-US', { 
      month: '2-digit', 
      day: '2-digit', 
      year: 'numeric' 
    });
  };

  // Helper function to convert date to input format (YYYY-MM-DD)
  const toInputDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  // Helper function to convert input date string to Date object
  const fromInputDate = (dateString) => {
    return new Date(dateString + 'T00:00:00');
  };

  useEffect(() => {
    fetchTasks();
  }, [contractId]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      setError(null);

      const apiBaseUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/client-dashboard/tasks/all/${contractId}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch tasks');
      }

      const allTasks = await response.json();
      console.log('All tasks data:', allTasks);
      console.log('Total tasks count:', allTasks.length);
      
      // Debug: Check list types and statuses
      const listTypes = [...new Set(allTasks.map(task => task.list_type))];
      const statuses = [...new Set(allTasks.map(task => task.status))];
      console.log('Available list types:', listTypes);
      console.log('Available statuses:', statuses);
      
      // Filter for todos only and allowed statuses
      const todoTasks = allTasks.filter(task => {
        const matchesListType = task.list_type === 'todos';
        const matchesStatus = ['working', 'waiting on client', 'delivered'].includes(task.status.toLowerCase());
        const notDeleted = !task.is_deleted;
        const notArchived = !task.is_archived;
        
        console.log(`Task "${task.name}": list_type=${task.list_type}, status=${task.status}, deleted=${task.is_deleted}, archived=${task.is_archived}`);
        console.log(`  - Matches list type: ${matchesListType}`);
        console.log(`  - Matches status: ${matchesStatus}`);
        console.log(`  - Not deleted: ${notDeleted}`);
        console.log(`  - Not archived: ${notArchived}`);
        
        return matchesListType && matchesStatus && notDeleted && notArchived;
      });
      
      console.log('Filtered todo tasks:', todoTasks);
      console.log('Todo tasks count:', todoTasks.length);

      setTasks(todoTasks);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError('Failed to load todos');
    } finally {
      setLoading(false);
    }
  };

  // Filter tasks based on selected status and date range
  const filteredTasks = tasks.filter(task => {
    // Status filter
    if (selectedStatus !== 'All') {
      const statusMap = {
        'Working': 'working',
        'Waiting on Client': 'waiting on client', 
        'Delivered': 'delivered'
      };
      if (task.status.toLowerCase() !== statusMap[selectedStatus]) {
        return false;
      }
    }

    // Date filter (based on due_date)
    if (task.due_date) {
      const taskDueDate = new Date(task.due_date);
      if (taskDueDate < startDate || taskDueDate > endDate) {
        return false;
      }
    }

    return true;
  }).sort((a, b) => {
    // Sort by due date descending (newest first)
    if (!a.due_date && !b.due_date) return 0;
    if (!a.due_date) return 1;
    if (!b.due_date) return -1;
    return new Date(b.due_date) - new Date(a.due_date);
  });

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'working': return '#2196F3'; // Blue
      case 'waiting on client': return '#FFC107'; // Yellow
      case 'delivered': return '#8A42EC'; // Purple
      default: return '#757575';
    }
  };

  const renderTaskCard = (task) => {
    // Get file type info for buttons
    const reviewFileInfo = task.link_to_review_tool ? getFileInfo(task.link_to_review_tool) : null;
    const deliverableFileInfo = task.link_to_final_deliverable ? getFileInfo(task.link_to_final_deliverable) : null;

    return (
      <Paper key={task.task_id} sx={{ mb: 2, p: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>{task.name}</Typography>
        {task.external_description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {task.external_description}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap', mt: 1.5, mb: 1.5 }}>
          {task.list_type !== 'todos' && (
            <Chip 
              icon={<Star sx={{ color: '#FFD700' }} />} 
              label={`${task.points ?? 0} Points`} 
              size="small" 
              sx={{ px: 1.5, py: 2 }} 
            />
          )}
          {task.due_date && (
            <Chip 
              icon={<CalendarToday sx={{ color: '#8A42EC' }} />} 
              label={`Due: ${formatDate(task.due_date)}`} 
              size="small" 
              sx={{ px: 1.5, py: 2 }} 
            />
          )}
          {task.status.toLowerCase() === 'delivered' && task.date_done && (
            <Chip 
              icon={<CalendarToday sx={{ color: '#8A42EC' }} />} 
              label={`Completed: ${formatDate(task.date_done)}`} 
              size="small" 
              sx={{ px: 1.5, py: 2 }} 
            />
          )}
          <Chip 
            label={task.status} 
            size="small" 
            sx={{ 
              backgroundColor: getStatusColor(task.status), 
              color: 'white',
              fontWeight: '500',
              px: 1.5, 
              py: 2
            }}
          />
          {task.link_to_review_tool && (
            <Button 
              href={task.link_to_review_tool} 
              target="_blank" 
              size="small" 
              startIcon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} className="client-dashboard-launch-icon" />} 
              sx={{ 
                minWidth: 0, 
                px: 1.5, 
                py: 2, 
                color: '#8A42EC', 
                fontWeight: 700, 
                textTransform: 'none'
              }}
              title={reviewFileInfo ? `Review (${reviewFileInfo.label})` : 'Review'}
            >
              Review{reviewFileInfo && reviewFileInfo.type !== 'unknown' && ` (${reviewFileInfo.type.toUpperCase()})`}
            </Button>
          )}
          {task.link_to_final_deliverable && (
            <Button 
              href={task.link_to_final_deliverable} 
              target="_blank" 
              size="small" 
              startIcon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} className="client-dashboard-launch-icon" />} 
              sx={{ 
                minWidth: 0, 
                px: 1.5, 
                py: 2, 
                color: '#8A42EC', 
                fontWeight: 700, 
                textTransform: 'none'
              }}
              title={deliverableFileInfo ? `Final deliverable (${deliverableFileInfo.label})` : 'Final deliverable'}
            >
              Final deliverable{deliverableFileInfo && deliverableFileInfo.type !== 'unknown' && ` (${deliverableFileInfo.type.toUpperCase()})`}
            </Button>
          )}
        </Box>
      </Paper>
    );
  };

  return (
    <>
      {/* Status Filter Tabs */}
      <Card className="details-card" style={{ marginBottom: '20px' }}>
        <Tabs
          value={selectedStatus}
          onChange={(event, newValue) => setSelectedStatus(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {statusOptions.map((status) => (
            <Tab
              key={status}
              label={status}
              value={status}
            />
          ))}
        </Tabs>
      </Card>

      {/* Date Filter Only (No Points Cards) */}
      <Card className="details-card" style={{ marginBottom: '20px' }}>
        <CardHeader title="Filter" />
        <Divider />
        <CardContent>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>Start Date</label>
              <input
                type="date"
                value={toInputDate(startDate)}
                onChange={(e) => setStartDate(fromInputDate(e.target.value))}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  minWidth: '150px',
                  backgroundColor: 'white'
                }}
              />
            </div>
            <Typography style={{ color: '#666', fontSize: '14px', marginTop: '16px' }}>to</Typography>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>End Date</label>
              <input
                type="date"
                value={toInputDate(endDate)}
                onChange={(e) => setEndDate(fromInputDate(e.target.value))}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  minWidth: '150px',
                  backgroundColor: 'white'
                }}
              />
            </div>
            <Typography variant="body2" style={{ color: '#666', marginLeft: '8px', marginTop: '16px' }}>
              Showing: {formatDateForDisplay(startDate)} - {formatDateForDisplay(endDate)}
            </Typography>
          </div>
        </CardContent>
      </Card>

      {/* Loading State */}
      {loading && <LinearProgress style={{ marginBottom: '20px' }} />}

      {/* Error State */}
      {error && (
        <Card style={{ padding: '20px', textAlign: 'center', marginBottom: '20px' }}>
          <Typography color="error">{error}</Typography>
        </Card>
      )}

      {/* Tasks Section */}
      {!loading && !error && (
        <Card className="details-card">
          <CardHeader title="Todo Tasks" />
          <Divider />
          <CardContent>
            {filteredTasks.length > 0 ? (
              <Box>
                {filteredTasks.map(renderTaskCard)}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6, color: '#888' }}>
                <Typography variant="h6" style={{ color: '#666', marginBottom: '8px' }}>
                  No todos found
                </Typography>
                <Typography variant="body2" style={{ color: '#999' }}>
                  {selectedStatus !== 'All' 
                    ? `No todos with status "${selectedStatus}" in the selected date range.`
                    : 'No todos found in the selected date range.'
                  }
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default ClientTodos; 